import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AdminTable, type ColDef } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import { PageHeader, AddButton, SaveButton, CancelButton, ViewButton, EditButton, DeleteButton } from "@/components/admin/shared/PageHeader";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import { TableSearch } from "@/components/admin/shared/TableSearch";
import { PennField, PennInput } from "@/components/ui";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { fastenerSchema, type FastenerFormData } from "@/types/admin/fastener.types";
import {
  useFasteners,
  useFastener,
  useCreateFastener,
  useUpdateFastener,
  useDeleteFastener,
} from "@/hooks/useFasteners";
import type { Fastener } from "@/api/fasteners";

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

type ModalMode = "add" | "edit" | "view";
type ModalState = { mode: ModalMode; item?: Fastener } | null;

const blank: FastenerFormData = { partNo: "", minSheetThickness: "" };

export const FastenersPage = ({
  title = "Fasteners",
  subtitle = "Dataset: PEM Fasteners",
}: {
  title?: string;
  subtitle?: string;
}) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const { searchInput, setSearchInput, search, clearSearch } = useDebouncedSearch(() => setPageIndex(0));

  const params = useMemo(
    () => ({ page: pageIndex + 1, limit: pageSize, search: search || undefined }),
    [pageIndex, pageSize, search],
  );

  const { data, isLoading, isFetching, isError, error } = useFasteners(params);
  const rows: Fastener[] = data?.data ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;
  const hasNext = pagination ? pagination.page < pagination.totalPages : false;

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<Fastener | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const editingPartNo = modal?.mode !== "add" ? (modal?.item?.partNo ?? null) : null;
  const detail = useFastener(editingPartNo);

  const createMutation = useCreateFastener();
  const updateMutation = useUpdateFastener();
  const deleteMutation = useDeleteFastener();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FastenerFormData>({ resolver: zodResolver(fastenerSchema) });

  const openAdd = () => {
    setFormError(null);
    reset(blank);
    setModal({ mode: "add" });
  };

  const fillForm = (item: Fastener) => {
    reset({
      partNo: item.partNo,
      minSheetThickness: item.minSheetThickness === null ? "" : Number(item.minSheetThickness),
    });
  };

  const openEdit = (item: Fastener) => { setFormError(null); fillForm(item); setModal({ mode: "edit", item }); };
  const openView = (item: Fastener) => { setFormError(null); fillForm(item); setModal({ mode: "view", item }); };

  const closeModal = () => { setModal(null); setFormError(null); };

  const onSubmit = (form: FastenerFormData) => {
    setFormError(null);
    const minSheetThickness =
      form.minSheetThickness === "" || form.minSheetThickness === undefined
        ? null
        : String(form.minSheetThickness);

    if (modal?.mode === "edit" && modal.item) {
      updateMutation.mutate(
        { partNo: modal.item.partNo, body: { minSheetThickness } },
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to save fastener.")),
        },
      );
    } else {
      createMutation.mutate(
        { partNo: form.partNo.trim(), minSheetThickness },
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to create fastener.")),
        },
      );
    }
  };

  const isView = modal?.mode === "view";
  const saving = createMutation.isPending || updateMutation.isPending;

  const cols: ColDef<Fastener>[] = [
    { header: "Part No", key: "partNo" },
    { header: "Min Sheet Thickness", render: (i) => i.minSheetThickness ?? "—" },
    { header: "Actions", render: (i) => <div className="flex space-x-2"><ViewButton onClick={() => openView(i)} /><EditButton onClick={() => openEdit(i)} /><DeleteButton onClick={() => setDeleteTarget(i)} /></div> },
  ];

  const titleMap = { add: `Add ${title} record`, edit: `Edit ${title} record`, view: `View ${title} record` };

  return (
    <>
      <PageHeader title={title} subtitle={`${subtitle} · ${total} records`} action={<AddButton onClick={openAdd} />} />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load fasteners{error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      {deleteError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          {deleteError}
        </div>
      )}

      <TableSearch
        value={searchInput}
        onChange={setSearchInput}
        onClear={clearSearch}
        placeholder="Search part no…"
      />

      <AdminTable columns={cols} rows={rows} keyExtractor={(i) => i.partNo} loading={isLoading} />

      <TablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowsOnPage={rows.length}
        hasNext={hasNext}
        busy={isFetching}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        onPageSizeChange={(size) => { setPageSize(size); setPageIndex(0); }}
      />

      <AdminModal
        open={modal !== null}
        onClose={closeModal}
        title={modal ? titleMap[modal.mode] : ""}
        footer={
          isView
            ? <CancelButton onClick={closeModal} />
            : <><CancelButton onClick={closeModal} /><SaveButton onClick={handleSubmit(onSubmit)} label={saving ? "Saving…" : "Save"} /></>
        }
      >
        {modal?.mode !== "add" && detail.isLoading ? (
          <div className="py-10 text-center text-blue_grey4 text-body">Loading…</div>
        ) : (
          <form className="grid grid-cols-1 sm:grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>
            {formError && (
              <div className="col-span-1 sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
                {formError}
              </div>
            )}
            <PennField label="Part No" htmlFor="partNo" required={!isView} error={!isView ? errors.partNo?.message : undefined}>
              <PennInput id="partNo" disabled={isView || modal?.mode === "edit"} error={!isView && !!errors.partNo} {...register("partNo")} />
            </PennField>
            <PennField label="Min Sheet Thickness" htmlFor="minSheetThickness" required={!isView} error={!isView ? errors.minSheetThickness?.message : undefined}>
              <PennInput id="minSheetThickness" type="number" step="0.001" disabled={isView} error={!isView && !!errors.minSheetThickness} {...register("minSheetThickness")} />
            </PennField>
          </form>
        )}
      </AdminModal>

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          setDeleteError(null);
          deleteMutation.mutate(deleteTarget.partNo, {
            onError: (e) => setDeleteError(apiErrorMessage(e, "Failed to delete fastener.")),
            onSettled: () => setDeleteTarget(null),
          });
        }}
        itemLabel={deleteTarget?.partNo}
      />
    </>
  );
};
